import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Col, Row } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { useAuth0 } from '@auth0/auth0-react';
import '../../styles/components/UserInventory.css';
import { FEATURE_FLAGS, isFeatureEnabled } from '../../config/featureFlags';

const MAX_IMAGES = 10;

const toDateInputValue = (value) => {
    if (!value) {
        return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }

    const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return offsetDate.toISOString().slice(0, 10);
};

const buildInitialFormData = (asset, listingIntent = false) => ({
    conversionRate: 64384,
    id: asset?.id ?? 0,
    assetName: asset?.assetName ?? '',
    assetDescription: asset?.assetDescription ?? '',
    productName: asset?.productName ?? '',
    productDescription: asset?.productDescription ?? '',
    quantity: Number.isInteger(asset?.quantity) ? asset.quantity : 0,
    availability: asset?.availability ?? true,
    location: asset?.location ?? '',
    acquisitionDate: toDateInputValue(asset?.acquisitionDate) || toDateInputValue(new Date().toISOString()),
    checkOutable: asset?.checkOutable ?? false,
    tradable: asset?.tradable ?? (listingIntent ? true : false),
    isFree: asset?.isFree ?? false,
    forSale: false,
    salePrice: '',
    recipientWalletAddress: asset?.recipientWalletAddress ?? asset?.RecipientWalletAddress ?? '',
    isDeleted: asset?.isDeleted ?? false,
    acceptsSheaf: false,
    sheafPrice: '',
    assetImages: [],
});

const getExistingImageList = (asset) => {
    const urls = [
        asset?.imageURL,
        ...(Array.isArray(asset?.additionalImages) ? asset.additionalImages : []),
        ...(Array.isArray(asset?.imageUrls) ? asset.imageUrls : []),
    ].filter(Boolean);

    return urls.slice(0, MAX_IMAGES).map((url) => ({
        id: `existing-${url}`,
        src: url,
    }));
};

const UserAssetModal = ({ show, onHide, asset, onAssetAdded, listingIntent = false }) => {
    const barterEnabled = isFeatureEnabled(FEATURE_FLAGS.marketplaceBarter);
    const [formData, setFormData] = useState(buildInitialFormData(asset, listingIntent));
    const [existingImages, setExistingImages] = useState(getExistingImageList(asset));
    const [deletedExistingImages, setDeletedExistingImages] = useState([]);
    const [imageError, setImageError] = useState('');
    const apiUrl = process.env.REACT_APP_API_URL;
    const { getAccessTokenSilently } = useAuth0();

    useEffect(() => {
        if (show) {
            const initialFormData = buildInitialFormData(asset, listingIntent);
            setFormData(initialFormData);
            setExistingImages(getExistingImageList(asset));
            setDeletedExistingImages([]);
            setImageError('');
        }
    }, [asset, listingIntent, show]);

    const newImagePreviews = useMemo(() => {
        return formData.assetImages.map((file) => ({
            id: `${file.name}-${file.size}`,
            src: URL.createObjectURL(file),
        }));
    }, [formData.assetImages]);

    useEffect(() => {
        return () => {
            newImagePreviews.forEach((preview) => URL.revokeObjectURL(preview.src));
        };
    }, [newImagePreviews]);

    const totalSelectedImages = existingImages.length + formData.assetImages.length;

    const handleInputChange = (e) => {
        const { name, value, files, type, checked } = e.target;

        if (name === 'assetImages') {
            const selectedFiles = Array.from(files ?? []);
            if (selectedFiles.length + existingImages.length > MAX_IMAGES) {
                setImageError(`You can upload a maximum of ${MAX_IMAGES} images per item.`);
                return;
            }

            setImageError('');
            setFormData((prevData) => ({
                ...prevData,
                assetImages: selectedFiles,
            }));
            return;
        }

        if (type === 'checkbox') {
            setFormData((prevData) => ({
                ...prevData,
                [name]: checked,
            }));
            return;
        }

        if (name === 'quantity') {
            if (!/^[0-9]*$/.test(value)) {
                return;
            }

            setFormData((prevData) => ({
                ...prevData,
                quantity: value === '' ? '' : parseInt(value, 10),
            }));
            return;
        }

        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const removeExistingImage = (id) => {
        const imageToDelete = existingImages.find((image) => image.id === id);
        if (!imageToDelete) {
            return;
        }

        setDeletedExistingImages((prev) => [...prev, imageToDelete.src]);
        setExistingImages((prev) => prev.filter((image) => image.id !== id));
    };

    const removeNewImage = (id) => {
        setFormData((prev) => ({
            ...prev,
            assetImages: prev.assetImages.filter((file) => `${file.name}-${file.size}` !== id),
        }));
    };

    const handleCloseModal = () => {
        onHide();
        if (onAssetAdded) {
            onAssetAdded();
        }
    };

    const handleSave = async () => {
        try {
            const token = await getAccessTokenSilently({
                authorizationParams: {
                    audience: process.env.REACT_APP_API_AUDIENCE,
                },
            });

            const askingPriceInBTC = formData.salePrice;

            const formDataToSend = new FormData();
            Object.entries(formData).forEach(([key, value]) => {
                if (['assetImages', 'conversionRate'].includes(key)) {
                    return;
                }

                if (key === 'salePrice') {
                    formDataToSend.append('salePrice', askingPriceInBTC || '');
                    return;
                }

                if (key === 'quantity') {
                    const safeQuantity = Number.isInteger(value) ? value : parseInt(value || '0', 10) || 0;
                    formDataToSend.append('quantity', safeQuantity);
                    return;
                }

                formDataToSend.append(key, value ?? '');
            });

            if (existingImages.length > 0) {
                formDataToSend.append('imageURL', existingImages[0].src);
            } else if (deletedExistingImages.length > 0 && formData.assetImages.length === 0) {
                formDataToSend.append('imageURL', '');
            }

            formData.assetImages.slice(0, MAX_IMAGES).forEach((file, index) => {
                if (index === 0) {
                    formDataToSend.append('AssetImage', file);
                }
                formDataToSend.append('AdditionalAssetImages', file);
            });

            const requestUrl = asset ? `${apiUrl}/api/assets/${asset.id}` : `${apiUrl}/api/assets`;
            const requestMethod = asset ? 'PUT' : 'POST';

            const response = await fetch(requestUrl, {
                method: requestMethod,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: formDataToSend,
            });

            if (!response.ok) {
                throw new Error(`Failed to save asset (${response.status})`);
            }

            handleCloseModal();
        } catch (error) {
            console.error('Error saving asset:', error);
        }
    };

    return (
        <Modal show={show} onHide={handleCloseModal} size="xl" dialogClassName="inventory-asset-modal">
            <Modal.Header closeButton className="inventory-asset-modal-header">
                <Modal.Title>{asset || !listingIntent ? (asset ? 'Edit inventory item' : 'Add inventory item') : 'List an item in the marketplace'}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="inventory-asset-modal-body">
                <Form className="inventory-asset-form-grid">
                    <section className="inventory-asset-section inventory-asset-section-core">
                        <h6 className="inventory-asset-section-title">Core Details</h6>
                        <div className="inventory-asset-fields-grid">
                            <Form.Group controlId="formAssetName" className="inventory-asset-field-full">
                                <Form.Label className="inventory-asset-field-label">Asset Name</Form.Label>
                                <Form.Control type="text" name="assetName" value={formData.assetName} onChange={handleInputChange} />
                            </Form.Group>

                            <Form.Group controlId="formAssetDescription" className="inventory-asset-field-full">
                                <Form.Label className="inventory-asset-field-label">Asset Description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    className="inventory-asset-description-input"
                                    name="assetDescription"
                                    value={formData.assetDescription}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>

                            <Form.Group controlId="formQuantity">
                                <Form.Label className="inventory-asset-field-label">Quantity</Form.Label>
                                <Form.Control type="number" step="1" min="0" inputMode="numeric" name="quantity" value={formData.quantity} onChange={handleInputChange} />
                            </Form.Group>
                            <Form.Group controlId="formLocation">
                                <Form.Label className="inventory-asset-field-label">Location</Form.Label>
                                <Form.Control type="text" name="location" value={formData.location} onChange={handleInputChange} />
                            </Form.Group>
                            <Form.Group controlId="formAcquisitionDate" className="inventory-asset-field-full">
                                <Form.Label className="inventory-asset-field-label">Acquisition Date</Form.Label>
                                <Form.Control type="date" name="acquisitionDate" value={formData.acquisitionDate} onChange={handleInputChange} />
                            </Form.Group>
                        </div>
                    </section>

                    <section className="inventory-asset-section inventory-asset-section-trade">
                        <div className="inventory-asset-marketplace-intro">
                            <span className="inventory-asset-section-title">Marketplace listing</span>
                            <h6>Give this item away or offer a trade</h6>
                            <p>
                                Personal listings are free or barter only. Select an option below to publish; leave both off to keep the item in your inventory.
                            </p>
                        </div>
                        <Row className="inventory-asset-check-row g-2">
                            <Col><Form.Check type="checkbox" label="Available" name="availability" checked={formData.availability} onChange={handleInputChange} /></Col>
                            <Col><Form.Check type="checkbox" label="Check-Outable" name="checkOutable" checked={formData.checkOutable} onChange={handleInputChange} /></Col>
                            {barterEnabled && <Col><Form.Check type="checkbox" label="Open to trade" name="tradable" checked={formData.tradable} onChange={handleInputChange} /></Col>}
                            <Col><Form.Check type="checkbox" label="Give away for free" name="isFree" checked={formData.isFree} onChange={handleInputChange} /></Col>
                            <Col><Form.Check type="checkbox" label="Deleted" name="isDeleted" checked={formData.isDeleted} onChange={handleInputChange} /></Col>
                        </Row>

                        {(formData.isFree || formData.tradable) && (
                            <div className="inventory-asset-fields-grid mt-2">
                                {(formData.isFree || formData.tradable) && (
                                    <>
                                        <Form.Group controlId="formProductName">
                                            <Form.Label>Product Name</Form.Label>
                                            <Form.Control type="text" name="productName" value={formData.productName} onChange={handleInputChange} />
                                        </Form.Group>
                                        <Form.Group controlId="formProductDescription" className="inventory-asset-field-full">
                                            <Form.Label>Product Description</Form.Label>
                                            <Form.Control as="textarea" rows={2} name="productDescription" value={formData.productDescription} onChange={handleInputChange} />
                                        </Form.Group>
                                    </>
                                )}
                            </div>
                        )}
                    </section>

                    <section className="inventory-asset-section inventory-asset-section-images">
                        <div className="inventory-asset-section-title-wrap">
                            <h6 className="inventory-asset-section-title">Images</h6>
                            <span className="inventory-asset-image-count">{totalSelectedImages}/{MAX_IMAGES}</span>
                        </div>
                        <Form.Group controlId="formAssetImage" className="mb-2">
                            <Form.Control type="file" name="assetImages" accept="image/*" multiple onChange={handleInputChange} />
                            {imageError && <div className="text-danger mt-2">{imageError}</div>}
                        </Form.Group>

                        <div className="inventory-asset-image-gallery">
                            {existingImages.map((image) => (
                                <div key={image.id} className="inventory-asset-image-tile">
                                    <img src={image.src} alt="Asset" className="inventory-asset-gallery-image" />
                                    <Button size="sm" variant="danger" className="inventory-asset-image-delete" onClick={() => removeExistingImage(image.id)}>
                                        <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                </div>
                            ))}
                            {newImagePreviews.map((preview) => (
                                <div key={preview.id} className="inventory-asset-image-tile">
                                    <img src={preview.src} alt="Asset upload preview" className="inventory-asset-gallery-image" />
                                    <Button size="sm" variant="danger" className="inventory-asset-image-delete" onClick={() => removeNewImage(preview.id)}>
                                        <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </section>

                    <div className="inventory-asset-actions">
                        <Button variant="primary" onClick={handleSave}>
                            {formData.isFree || formData.tradable ? 'Save and list item' : 'Save Asset'}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default UserAssetModal;
